import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Observable } from 'rxjs';
import { PortalScope } from '../../login/login.component';
import { ESignCompletionContext } from '../../models/e-sign/e-sign-completion-context.model';
import { PortalServiceFactory } from '../../services/portal-service.factory';
import { UtilsService } from '../../services/utils.service';

// Landing page for vendors that pin their end-of-signing redirect to the signing order instead of to one
// signer's signing session - SignNow - so the redirect url carries no task, token or order of its own. It
// renders inside the signing iframe and hands the outcome to the portal page hosting that iframe, which
// knows which task and signer the session belonged to and reports the outcome to the api.
//
// The api is configured with one static host for this page, so for a company whose portal is served from
// another domain this page is cross-origin to that portal: it cannot be handed the outcome that way, since a
// cross-origin parent can neither be targeted by origin nor be trusted to be a portal at all. It instead asks
// the parent for the signer's auth token and completion context - the handshake the Encompass POS eClose flow
// already uses, which needs no cross-site cookie - and reports the outcome to the api itself.

// What the signer is told. Only 'signed' states that the document was signed, and every path that ends with
// the api not knowing the outcome ends on 'notRecorded' rather than on a spinner that never resolves.
export type ESignVendorConfirmationStatus = 'reporting' | 'signed' | 'declined' | 'cancelled' | 'notRecorded';

// How long to wait for the embedding portal to answer the token request before telling the signer the outcome
// went unrecorded. Paired with the deliberate delay the e-sign responders answer on, so this has to stay
// comfortably longer than that delay - shortening it below that turns every signing into a false 'notRecorded'.
const TOKEN_RESPONSE_TIMEOUT_MS = 10000;

@Component({
    selector: 'e-sign-vendor-confirmation',
    templateUrl: './e-sign-vendor-confirmation.component.html',
    standalone: false
})
export class ESignVendorConfirmationComponent implements OnInit, OnDestroy {

  status: ESignVendorConfirmationStatus = 'reporting';

  private _action: string | null = null;
  private _tokenResponseHandlerRegistered: boolean = false;
  private _tokenResponseTimeoutId: ReturnType<typeof setTimeout> | null = null;

  private readonly _tokenResponseHandler = (event: MessageEvent) => {
    // Only the window this page asked for a token can answer, and only an answer carrying the completion
    // context is one this page can act on - the Encompass POS flow answers the same request with a bare token.
    if (event.source !== window.parent) {
      return;
    }
    if (event.data?.authTokenDenied) {
      this.stopWaitingForTokenResponse();
      this.status = 'notRecorded';
      return;
    }
    if (!event.data?.authCompletionToken) {
      return;
    }
    const completionContext = event.data.eSignCompletionContext as ESignCompletionContext | undefined;
    if (!completionContext?.loanDocTaskId) {
      return;
    }

    this.stopWaitingForTokenResponse();
    this.reportSigningOutcome(event.origin, event.data.authCompletionToken, completionContext);
  };

  constructor(
    private readonly _activatedRoute: ActivatedRoute,
    private readonly _portalServiceFactory: PortalServiceFactory,
    private readonly _utilsService: UtilsService
  ) { }

  ngOnInit(): void {
    this._action = this._activatedRoute.snapshot.queryParamMap.get('event')
      ?? this._activatedRoute.snapshot.queryParamMap.get('esign.status');

    if (this.isParentSameOrigin()) {
      // The portal page hosting this iframe is served from this same app origin, so target it explicitly
      // instead of '*' to avoid leaking the vendor signing return parameters to any embedding window.
      window.parent.postMessage(
        {
          eSignVendorSigningEnded: true,
          action: this._action,
          vendorQueryParameters: this._utilsService.getAllQueryParameters(),
        },
        window.location.origin
      );
      this.status = this.outcomeStatus();
      return;
    }

    if (window.parent === window) {
      // Opened outside the signing iframe - there is nobody to ask for the token, so this session's outcome
      // will not reach the api from here.
      this.status = 'notRecorded';
      return;
    }

    this.startWaitingForTokenResponse();
    // The parent's origin is unknown until it answers, and this request carries nothing sensitive, so it is
    // the one message broadcast to the embedding window; everything after this is targeted at that origin.
    window.parent.postMessage({ authTokenRequested: true }, '*');
  }

  ngOnDestroy(): void {
    this.stopWaitingForTokenResponse();
  }

  // The vendor reports what ended the session in the same query parameter the api reads to decide what to
  // record, so the signer is never told they signed when they declined or walked away.
  private outcomeStatus = (): ESignVendorConfirmationStatus => {
    switch (this._action) {
      case 'decline':
        return 'declined';
      case 'cancel':
        return 'cancelled';
      default:
        return 'signed';
    }
  };

  private reportSigningOutcome = (
    parentOrigin: string,
    authToken: string,
    completionContext: ESignCompletionContext
  ) => {
    const portalService = this._portalServiceFactory.createByScope(
      completionContext.portalScope === PortalScope.Agent ? PortalScope.Agent : PortalScope.Borrower
    );
    const vendorQueryParameters = this._utilsService.getAllQueryParameters();

    const outcomeReported: Observable<unknown> = completionContext.publicToken
      ? portalService.onSignerViewSubmittedForAnonymousEsign(
        completionContext.publicToken,
        completionContext.loanDocTaskId,
        this._action,
        authToken,
        vendorQueryParameters
      )
      : portalService.onSignerViewSubmittedWithExternalToken(
        completionContext.loanDocTaskId,
        this._action,
        authToken,
        vendorQueryParameters
      );

    outcomeReported.subscribe({
      next: (response) => {
        this.status = this.outcomeStatus();
        window.parent.postMessage(
          {
            shouldCloseEsignDocIframe: true,
            orderId: completionContext.orderId,
            response: response,
          },
          parentOrigin
        );
      },
      error: (error) => {
        this.status = 'notRecorded';
        console.error(error?.error?.message ?? 'E-sign confirmation failed.');
      },
    });
  };

  private isParentSameOrigin = (): boolean => {
    if (window.parent === window) {
      return false;
    }
    try {
      // Reading the parent's location throws for a cross-origin parent, which is the only way to tell the two
      // apart - a cross-origin window exposes nothing else to compare.
      return window.parent.location.origin === window.location.origin;
    } catch {
      return false;
    }
  };

  private startWaitingForTokenResponse = () => {
    if (this._tokenResponseHandlerRegistered) {
      return;
    }
    window.addEventListener('message', this._tokenResponseHandler, false);
    this._tokenResponseHandlerRegistered = true;

    // A parent that never answers - an older portal build, or a window that is not one of our portals at all -
    // would otherwise leave the signer watching a spinner forever.
    this._tokenResponseTimeoutId = setTimeout(() => {
      this.stopWaitingForTokenResponse();
      this.status = 'notRecorded';
    }, TOKEN_RESPONSE_TIMEOUT_MS);
  };

  private stopWaitingForTokenResponse = () => {
    if (this._tokenResponseTimeoutId !== null) {
      clearTimeout(this._tokenResponseTimeoutId);
      this._tokenResponseTimeoutId = null;
    }
    if (!this._tokenResponseHandlerRegistered) {
      return;
    }
    window.removeEventListener('message', this._tokenResponseHandler, false);
    this._tokenResponseHandlerRegistered = false;
  };
}
